/**
 * Centralized data mapping utilities for transforming API responses to UI-expected format
 */

/**
 * Map snake_case to camelCase
 */
export const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
};

/**
 * Convert object keys from snake_case to camelCase
 */
export const snakeToCamel = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(snakeToCamel);

  const camelObj: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = toCamelCase(key);
    camelObj[camelKey] = snakeToCamel(value);
  }
  return camelObj;
};

/**
 * Format date string to display format
 */
export const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (e) {
    return dateString;
  }
};

/**
 * Format date string to Month Year format (e.g. Jan 2024)
 */
export const formatMonthYear = (dateString?: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
    });
  } catch (e) {
    return dateString;
  }
};

/**
 * Format ISO datetime to date string (YYYY-MM-DD)
 */
export const formatISODate = (isoString?: string | null): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toISOString().split('T')[0];
  } catch (e) {
    return isoString;
  }
};

/**
 * Format ISO datetime to time string (HH:MM)
 */
export const formatISOTime = (isoString?: string | null): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toTimeString().slice(0, 5);
  } catch (e) {
    return isoString;
  }
};

/**
 * Parse date and time strings to ISO datetime
 */
export const parseDateTimeToISO = (date?: string, time?: string): string | null => {
  if (!date || !time) return null;

  // Basic validation for YYYY-MM-DD and HH:mm
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const timeRegex = /^\d{2}:\d{2}$/;

  if (!dateRegex.test(date) || !timeRegex.test(time)) {
    return null;
  }

  try {
    const d = new Date(`${date}T${time}:00`);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch (e) {
    return null;
  }
};

/**
 * Format 24-hour time string (HH:MM) to 12-hour format (h:MM AM/PM)
 */
export const formatTime = (timeString?: string | null): string => {
  if (!timeString) return '';
  try {
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours, 10);
    const minuteStr = minutes || '00';

    if (isNaN(hour24)) return timeString;

    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    const period = hour24 >= 12 ? 'PM' : 'AM';

    return `${hour12}:${minuteStr}${period}`;
  } catch (e) {
    return timeString;
  }
};
